// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { dataTypeKind, docPropsCoreXmlPath, docPropsRootElement, element, elementAttributes, falseStr, textResultType, trueStr, xmlTextResultType, } from "./constants";
import { DataTypes } from "../types";
var createOrUpdateProperty = function (doc, parent, property, value) {
    if (value === undefined) {
        return;
    }
    var elements = parent.getElementsByTagName(property);
    if ((elements === null || elements === void 0 ? void 0 : elements.length) === 0) {
        var newElement = doc.createElement(property);
        newElement.textContent = value;
        parent.appendChild(newElement);
    }
    else if (elements.length > 1) {
        throw new Error("Invalid DocProps core.xml, multiple ".concat(property, " elements"));
    }
    else if ((elements === null || elements === void 0 ? void 0 : elements.length) > 0) {
        elements[0].textContent = value;
    }
};
var getDocPropsProperties = function (zip) { return __awaiter(void 0, void 0, void 0, function () {
    var docPropsCoreXmlString, parser, doc, properties;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0: return [4 /*yield*/, ((_a = zip.file(docPropsCoreXmlPath)) === null || _a === void 0 ? void 0 : _a.async(textResultType))];
            case 1:
                docPropsCoreXmlString = _b.sent();
                if (docPropsCoreXmlString === undefined) {
                    throw new Error("DocProps core.xml was not found in template");
                }
                parser = new DOMParser();
                doc = parser.parseFromString(docPropsCoreXmlString, xmlTextResultType);
                properties = doc.getElementsByTagName(docPropsRootElement).item(0);
                if (properties === null) {
                    throw new Error("Invalid DocProps core.xml");
                }
                return [2 /*return*/, { doc: doc, properties: properties }];
        }
    });
}); };
var getCellReferenceAbsolute = function (col, row) {
    // 65 is the ascii value of first column 'A'
    return "$" + String.fromCharCode(col + 65) + "$" + row.toString();
};
var getCellReferenceRelative = function (col, row) {
    // 65 is the ascii value of first column 'A'
    return String.fromCharCode(col + 65) + row.toString();
};
var getTableReference = function (numberOfCols, numberOfRows) {
    return "A1:".concat(getCellReferenceRelative(numberOfCols, numberOfRows));
};
var createCellElement = function (doc, colIndex, rowIndex, data) {
    var cell = doc.createElementNS(doc.documentElement.namespaceURI, element.kindCell);
    cell.setAttribute(elementAttributes.row, getCellReferenceRelative(colIndex, rowIndex + 1));
    var cellData = doc.createElementNS(doc.documentElement.namespaceURI, element.cellValue);
    updateCellData(data, cell, cellData, rowIndex === 0);
    cell.appendChild(cellData);
    return cell;
};
var updateCellData = function (data, cell, cellData, rowHeader) {
    switch (resolveType(data, rowHeader)) {
        case DataTypes.string:
            cell.setAttribute(element.text, dataTypeKind.string);
            break;
        case DataTypes.number:
            cell.setAttribute(element.text, dataTypeKind.number);
            break;
        case DataTypes.boolean:
            cell.setAttribute(element.text, dataTypeKind.boolean);
            break;
    }
    if (data.startsWith(" ") || data.endsWith(" ")) {
        cellData.setAttribute(elementAttributes.space, "preserve");
    }
    cellData.textContent = data;
};
var resolveType = function (originalData, rowHeader) {
    var data = originalData;
    if ((rowHeader) || (data.trim() === "")) {
        // Headers and whitespace should be string by default.
        return DataTypes.string;
    }
    var dataType = isNaN(Number(data)) ? DataTypes.string : DataTypes.number;
    if (dataType == DataTypes.string) {
        if (data.trim() == trueStr || data.trim() == falseStr) {
            dataType = DataTypes.boolean;
        }
    }
    return dataType;
};
export default {
    createOrUpdateProperty: createOrUpdateProperty,
    getDocPropsProperties: getDocPropsProperties,
    getCellReferenceRelative: getCellReferenceRelative,
    getCellReferenceAbsolute: getCellReferenceAbsolute,
    createCell: createCellElement,
    getTableReference: getTableReference,
    updateCellData: updateCellData,
    resolveType: resolveType,
};
